import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from './lib/supabase';
import { DOMAINS, MONTHS } from './lib/constants';
import { rowToTask, taskToRow, isOverdue, diffTask, statusLabel } from './lib/utils';

import LoginScreen  from './components/LoginScreen';
import Topbar       from './components/Topbar';
import KpiStrip     from './components/KpiStrip';
import DomainSection from './components/DomainSection';
import TaskModal    from './components/TaskModal';
import WhoModal     from './components/WhoModal';
import ActivityPane from './components/ActivityPane';


export default function App() {
  const [authed,    setAuthed]    = useState(false);
  const [tasks,     setTasks]     = useState([]);
  const [activity,  setActivity]  = useState([]);
  const [month,     setMonth]     = useState('may');
  const [filters,   setFilters]   = useState({ search:'', owner:'', priority:'', status:'' });
  const [syncLabel, setSyncLabel] = useState('Connecting…');
  const [syncState, setSyncState] = useState('idle');
  const [isMobile, setIsMobile]   = useState(window.innerWidth <= 768);

  // Modals
  const [taskModal, setTaskModal]   = useState({ open:false, task:null, domain:null });
  const [whoModal,  setWhoModal]    = useState({ open:false, saving:false });
  const [pendingSave, setPendingSave] = useState(null);
  const [activityOpen, setActivityOpen] = useState(false);

  const setSync = (label, state='idle') => { setSyncLabel(label); setSyncState(state); };

  // Handle window resize for responsive design
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // ── BOOTSTRAP ──
  const bootstrap = useCallback(async () => {
    setSync('Loading…');
    const { data: rows, error } = await supabase.from('tasks').select('*').order('updated_at', { ascending:false });
    if (error) { setSync('Offline', 'error'); return; }
    setTasks((rows || []).map(rowToTask));

    const { data: acts } = await supabase.from('activity').select('*').order('at', { ascending:false }).limit(100);
    setActivity((acts || []).map(a => ({ who:a.who, action:a.action, at:new Date(a.at).getTime() })));

    setSync('Live · synced', 'live');
  }, []);

  // ── REALTIME SUBSCRIPTION ──
  useEffect(() => {
    const channel = supabase
      .channel('tasks-feed')
      .on('postgres_changes', { event:'*', schema:'public', table:'tasks' }, payload => {
        const t = payload.new && rowToTask(payload.new);
        setTasks(prev => {
          if (payload.eventType === 'DELETE') return prev.filter(x => x.id !== payload.old.id);
          const idx = prev.findIndex(x => x.id === t.id);
          return idx >= 0 ? prev.map((x, i) => i===idx ? t : x) : [t, ...prev];
        });
        setSync('Live · synced', 'live');
      })
      .on('postgres_changes', { event:'INSERT', schema:'public', table:'activity' }, payload => {
        const a = payload.new;
        setActivity(prev => [{ who:a.who, action:a.action, at:new Date(a.at).getTime() }, ...prev].slice(0, 100));
      })
      .subscribe(status => {
        if (status === 'SUBSCRIBED') setSync('Live · synced', 'live');
        else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') setSync('Reconnecting…', 'error');
      });

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  // ── AUTH ──
  useEffect(() => {
    supabase.auth.getSession().then(({ data:{ session } }) => {
      if (session) { setAuthed(true); bootstrap(); }
    });
    const { data:{ subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_OUT' || !session) { setAuthed(false); setTasks([]); setActivity([]); }
    });
    return () => subscription.unsubscribe();
  }, [bootstrap]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLogin = () => { setAuthed(true); bootstrap(); };
  const handleSignOut = async () => {
    if (!window.confirm('Sign out of the tracker?')) return;
    await supabase.auth.signOut();
  };

  // ── KEYBOARD ──
  useEffect(() => {
    const handler = e => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') { e.preventDefault(); openNewTask(); }
      if (e.key === 'Escape') { setTaskModal(m => ({...m,open:false})); setWhoModal(m=>({...m,open:false})); setActivityOpen(false); }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  // ── FILTERS ──
  const filteredTasks = () => tasks.filter(t => {
    if (t.month !== month) return false;
    const f = filters;
    if (f.owner && t.owner !== f.owner) return false;
    if (f.priority && t.priority !== f.priority) return false;
    if (f.status) {
      if (f.status === 'overdue' && !isOverdue(t)) return false;
      else if (f.status !== 'overdue' && t.status !== f.status) return false;
    }
    if (f.search) {
      const q = f.search.toLowerCase();
      if (!`${t.title} ${t.desc||''} ${t.owner} ${t.reason||''}`.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  // ── TASK ACTIONS ──
  const openNewTask = (domain) => setTaskModal({ open:true, task:null, domain: domain||null });
  const openEdit    = (id) => {
    const t = tasks.find(x => x.id === id);
    if (t) setTaskModal({ open:true, task:JSON.parse(JSON.stringify(t)), domain:null });
  };
  const closeTask   = () => setTaskModal(m => ({...m, open:false}));

  const handleTaskSave = (form) => {
    const isNew = !form.id;
    const draft = { ...form, id: form.id || ('t' + Date.now().toString(36)), updatedAt: Date.now() };
    setPendingSave({ task:draft, isNew, original: isNew ? null : tasks.find(t=>t.id===form.id), kind: isNew?'create':'edit' });
    closeTask();
    setWhoModal({ open:true, saving:false });
  };

  const cycleStatus = (id) => {
    const t = tasks.find(x => x.id === id);
    if (!t) return;
    const flow = { pending:'in_progress', in_progress:'done', done:'pending' };
    const next = flow[t.status];
    if (next === 'in_progress' && !t.deadline) {
      openEdit(id);
      return;
    }
    const draft = { ...t, status:next };
    setPendingSave({ task:draft, isNew:false, original:{...t}, kind:'cycle' });
    setWhoModal({ open:true, saving:false });
  };

  const deleteTask = (id) => {
    const t = tasks.find(x => x.id === id);
    if (!t) return;
    if (!window.confirm(`Delete "${t.title}"?`)) return;
    setPendingSave({ task:t, isNew:false, original:{...t}, kind:'delete' });
    setWhoModal({ open:true, saving:false });
  };

  const confirmWho = async (who) => {
    setWhoModal(m => ({ ...m, saving:true }));
    try {
      const { task, isNew, original, kind } = pendingSave;
      task.updatedBy = who;

      if (kind === 'delete') {
        const { error } = await supabase.from('tasks').delete().eq('id', task.id);
        if (error) throw error;
        await supabase.from('activity').insert({ who, action:`deleted "${task.title}"`, task_id:null });
      } else if (isNew) {
        const { error } = await supabase.from('tasks').insert(taskToRow(task));
        if (error) throw error;
        const domainName = DOMAINS.find(d=>d.id===task.domain)?.name || task.domain;
        await supabase.from('activity').insert({ who, action:`created "${task.title}" in ${domainName}`, task_id:task.id });
      } else {
        const { error } = await supabase.from('tasks').update(taskToRow(task)).eq('id', task.id);
        if (error) throw error;
        const changes = diffTask(original, task);
        const verb = kind === 'cycle'
          ? `marked "${task.title}" as ${statusLabel(task.status)}`
          : `updated "${task.title}"${changes ? ` — ${changes}` : ''}`;
        await supabase.from('activity').insert({ who, action:verb, task_id:task.id });
      }

      setWhoModal({ open:false, saving:false });
      setPendingSave(null);
      setSync('Live · synced', 'live');
    } catch (err) {
      alert('Save failed: ' + (err.message || err));
      setWhoModal(m => ({ ...m, saving:false }));
      setSync('Error', 'error');
    }
  };


  // ── RENDER ──
  if (!authed) return <LoginScreen onLogin={handleLogin} />;

  const shown = filteredTasks();
  const monthTasks = tasks.filter(t => t.month === month);
  const overdueCount = monthTasks.filter(t => isOverdue(t)).length;
  const hStyles = getResponsiveStyles(isMobile);

  return (
    <>
      <Topbar
        onActivity={() => setActivityOpen(true)}
        onAddTask={openNewTask}
        onSignOut={handleSignOut}
        syncLabel={syncLabel}
        syncState={syncState}
      />

      {/* ── HEADER ── */}
      <section style={hStyles.header}>
        <div>
          <div style={hStyles.eyebrow}>
            <span style={hStyles.dot} />
            Live · Single Source of Truth · Synced to Supabase
          </div>
          <h1 style={hStyles.h1}>One screen. <em style={{ fontStyle:'italic', color:'#0284C7' }}>Every move</em>. The whole team.</h1>
          <p style={hStyles.lede}>Not for control — for clarity. See what everyone's working on, why something's delayed, and what's shipping next. <strong style={{color:'#0A2540'}}>If it's in progress, it has a deadline. If it slipped, it has a reason.</strong></p>
        </div>
        <div style={hStyles.actions}>
          <button
            onClick={openNewTask}
            style={hStyles.addBtn}
            onMouseEnter={e => { e.currentTarget.style.background='#000'; e.currentTarget.style.transform='translateY(-1px)'; }}
            onMouseLeave={e => { e.currentTarget.style.background='#0A2540'; e.currentTarget.style.transform='none'; }}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" width="14" height="14">
              <path d="M12 5v14M5 12h14"/>
            </svg>
            New task
          </button>
        </div>
      </section>

      {/* ── OVERDUE BANNER ── */}
      {overdueCount > 0 && (
        <div style={hStyles.banner}>
          <svg style={{ color:'#B45309', flexShrink:0 }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" width="18" height="18">
            <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
            <path d="M12 9v4M12 17h.01"/>
          </svg>
          <span><strong style={{color:'#78350F'}}>{overdueCount} task{overdueCount>1?'s':''} overdue</strong> · ETA & reason mandatory before save</span>
        </div>
      )}

      {/* ── KPI STRIP ── */}
      <KpiStrip tasks={tasks} month={month} />

      {/* ── TOOLBAR ── */}
      <div style={hStyles.toolbar}>
        {/* Month tabs */}
        <div style={hStyles.monthTabs}>
          {MONTHS.map(m => {
            const cnt = tasks.filter(t => t.month===m).length;
            return (
              <button
                key={m}
                onClick={() => setMonth(m)}
                style={{ ...hStyles.monthTab, ...(month===m ? hStyles.monthTabActive : {}) }}
              >
                {m.charAt(0).toUpperCase()+m.slice(1)}
                <span style={{ ...hStyles.badge, ...(month===m ? hStyles.badgeActive : {}) }}>{cnt}</span>
              </button>
            );
          })}
        </div>

        {/* Search */}
        <div style={{ position:'relative', flex:1, minWidth:240, maxWidth:340 }}>
          <svg style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)', color:'#7A8BA3' }} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" width="14" height="14">
            <circle cx="11" cy="11" r="7"/><path d="m21 21-4.3-4.3"/>
          </svg>
          <input
            style={hStyles.search}
            placeholder="Search tasks, owners, reasons…"
            value={filters.search}
            onChange={e => setFilters(f => ({...f, search:e.target.value}))}
          />
        </div>

        {/* Filters */}
        <div style={{ display:'flex', gap:8 }}>
          {[
            { id:'owner',    placeholder:'All owners',    opts:['Arshad','Fahad','Prateek','Utkarsh','Zeba','Azmath'] },
            { id:'priority', placeholder:'All priorities', opts:['P0','P1','P2','P3'] },
            { id:'status',   placeholder:'All statuses',  opts:['pending','in_progress','done','overdue'] },
          ].map(f => (
            <select
              key={f.id}
              style={hStyles.filterSelect}
              value={filters[f.id]}
              onChange={e => setFilters(prev => ({...prev,[f.id]:e.target.value}))}
            >
              <option value="">{f.placeholder}</option>
              {f.opts.map(o => <option key={o} value={o}>{o}</option>)}
            </select>
          ))}
        </div>
      </div>

      {/* ── DOMAINS ── */}
      <main style={{ padding: isMobile ? '0 16px 40px' : '0 32px 60px' }}>
        {DOMAINS.map(d => (
          <DomainSection
            key={d.id}
            domain={d}
            tasks={shown.filter(t => t.domain===d.id)}
            allTasks={tasks}
            month={month}
            onEdit={openEdit}
            onCycle={cycleStatus}
            onDelete={deleteTask}
            onAddTask={openNewTask}
          />
        ))}
      </main>

      <footer style={hStyles.footer}>
        <div>DialyGo · Built with care · <span style={{ fontFamily:"'JetBrains Mono',monospace" }}>CTRL/⌘ + K</span> for quick add</div>
        <div style={{ display:'flex', alignItems:'center', gap:14, fontSize:11.5, color:'#7A8BA3', fontFamily:"'JetBrains Mono',monospace" }}>
          <span style={{ display:'flex', alignItems:'center', gap:6 }}>
            <span style={{ width:6, height:6, borderRadius:'50%', background: syncState==='live' ? '#10B981' : '#94A3B8', display:'inline-block', animation: syncState==='live' ? 'pulse 2s infinite' : 'none' }} />
            {syncLabel}
          </span>
          <span>v0.2 · Live on Supabase</span>
        </div>
      </footer>

      {/* ── MODALS ── */}
      <TaskModal
        open={taskModal.open}
        task={taskModal.task}
        defaultDomain={taskModal.domain}
        defaultMonth={month}
        onClose={closeTask}
        onSave={handleTaskSave}
      />

      <WhoModal
        open={whoModal.open}
        saving={whoModal.saving}
        onClose={() => setWhoModal({ open:false, saving:false })}
        onConfirm={confirmWho}
      />

      <ActivityPane
        open={activityOpen}
        activity={activity}
        onClose={() => setActivityOpen(false)}
      />

    </>
  );
}

const getResponsiveStyles = (isMobileView) => ({
  header: {
    padding: isMobileView ? '20px 16px 16px' : '36px 32px 24px',
    display:'flex',
    flexDirection: isMobileView ? 'column' : 'row',
    alignItems: isMobileView ? 'flex-start' : 'flex-end',
    justifyContent:'space-between',
    gap: isMobileView ? 16 : 32,
    flexWrap:'wrap'
  },
  eyebrow: { fontSize: isMobileView ? 9 : 11, textTransform:'uppercase', letterSpacing:'0.16em', color:'#0284C7', fontWeight:500, fontFamily:"'JetBrains Mono',monospace", marginBottom:8, display:'flex', alignItems:'center', gap:10 },
  dot: { width:7, height:7, borderRadius:'50%', background:'#10B981', display:'inline-block', animation:'pulse 2s infinite' },
  h1: { fontFamily:"'Fraunces',serif", fontWeight:500, fontSize: isMobileView ? 28 : 38, margin:0, letterSpacing:'-0.025em', lineHeight:1.05 },
  lede: { marginTop:10, color:'#3D5878', fontSize: isMobileView ? 13 : 14.5, maxWidth:560 },
  actions: { display:'flex', flexDirection: isMobileView ? 'column' : 'row', gap: isMobileView ? 8 : 10, alignItems: isMobileView ? 'stretch' : 'center', width: isMobileView ? '100%' : 'auto' },
  addBtn: {
    display:'inline-flex', alignItems:'center', justifyContent:'center', gap:8, padding: isMobileView ? '10px 14px' : '11px 18px',
    borderRadius:10, fontSize: isMobileView ? 12 : 13, fontWeight:500, cursor:'pointer', border:'none',
    background:'#0A2540', color:'white', transition:'all .15s ease',
  },
  banner: {
    margin: isMobileView ? '0 16px 20px' : '0 32px 28px',
    padding: isMobileView ? '12px 14px' : '14px 18px',
    background:'linear-gradient(90deg,rgba(254,243,199,.6),rgba(254,243,199,.2))',
    border:'1px solid #FCD34D', borderLeft:'3px solid #F59E0B', borderRadius:10,
    display:'flex', alignItems:'center', gap: isMobileView ? 8 : 12, fontSize: isMobileView ? 12 : 13,
    flexWrap: 'wrap',
  },
  toolbar: {
    margin: isMobileView ? '0 16px 16px' : '0 32px 18px',
    display:'flex',
    flexDirection: isMobileView ? 'column' : 'row',
    alignItems:'stretch',
    gap: isMobileView ? 10 : 12,
    flexWrap:'wrap'
  },
  monthTabs: { display:'flex', background:'white', border:'1px solid #E4ECF4', borderRadius:12, padding:4, gap:2, overflowX: isMobileView ? 'auto' : 'visible', minWidth: 0 },
  monthTab: { padding: isMobileView ? '6px 10px' : '7px 14px', borderRadius:8, fontSize: isMobileView ? 11 : 12.5, fontWeight:500, color:'#3D5878', background:'none', border:'none', cursor:'pointer', display:'flex', alignItems:'center', gap:6, transition:'all .15s ease', whiteSpace: 'nowrap' },
  monthTabActive: { background:'#0A2540', color:'white' },
  badge: { background:'rgba(14,165,233,.15)', color:'#0284C7', fontSize: isMobileView ? 8 : 10, padding:'1px 6px', borderRadius:99, fontWeight:600, fontFamily:"'JetBrains Mono',monospace" },
  badgeActive: { background:'rgba(255,255,255,.2)', color:'white' },
  search: { width:'100%', padding: isMobileView ? '8px 10px 8px 30px' : '9px 12px 9px 34px', background:'white', border:'1px solid #E4ECF4', borderRadius:10, fontSize: isMobileView ? 12 : 13, outline:'none', transition:'all .15s ease' },
  filterSelect: { padding: isMobileView ? '8px 28px 8px 10px' : '9px 32px 9px 12px', background:'white', border:'1px solid #E4ECF4', borderRadius:10, fontSize: isMobileView ? 11 : 12.5, color:'#0A2540', cursor:'pointer', outline:'none', appearance:'none', backgroundImage:`url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='%237A8BA3' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`, backgroundRepeat:'no-repeat', backgroundPosition:'right 10px center', flex: isMobileView ? 1 : 'auto' },
  footer: {
    margin: isMobileView ? '30px 16px 16px' : '40px 32px 20px',
    padding: isMobileView ? '12px 0' : '18px 0',
    borderTop:'1px solid #E4ECF4',
    display:'flex',
    flexDirection: isMobileView ? 'column' : 'row',
    justifyContent:'space-between',
    alignItems: isMobileView ? 'flex-start' : 'center',
    fontSize: isMobileView ? 10 : 11.5,
    color:'#7A8BA3',
    fontFamily:"'JetBrains Mono',monospace",
    gap: isMobileView ? 8 : 0,
  },
});

